var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/**
 * VA-28665 — Barge-in tuning bench (admin "VAD / Turn Detection" tab).
 *
 * The interruption is decided in the BROWSER: the same Silero model as the backend, with its
 * own thresholds (see context/silero-shadow.ts). This panel runs that exact detector on the
 * admin's microphone, with the values on screen, and shows the moment it WOULD cut the AI
 * off — so you can have someone talk behind you and see whether it interrupts, before saving.
 *
 * "Play AI voice" plays the real interviewer voice (the default provider's test TTS) through
 * this page while the meter runs, the way an interview plays it, so you can also check that
 * the AI's own voice does not interrupt itself. Falls back to the browser's built-in voice if
 * no provider can synthesize.
 *
 * Saving writes the barge-in values only (partial update); the next interviews use them.
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2, Mic, RotateCcw, Save, Square, Volume2, VolumeX } from 'lucide-react';
import { useToast } from '@/hoc';
import { BARGE_IN_BOUNDS, DEFAULT_BARGE_IN_CONFIG, normalizeBargeInConfig, } from '@/context/barge-in-config';
import { SileroShadowController } from '@/context/silero-shadow';
import { pcm16Base64ToAudioBuffer } from '@/context/realtime/audio-utils';
import { Button } from '../ui/button';
import { describeFloor, fromAdminSettings, toAdminPayload } from './barge-in-admin';
import { aiVoiceCacheKey, readCachedAiVoice, writeCachedAiVoice } from './ai-voice-cache';
import { useAdminVoiceProviders, useSaveVadSettings, useVadSettings, voiceAdminClient, } from './voice-admin-api';
const AI_PHRASE = 'Thank you for that answer. Could you tell me about a project where you had to debug a ' +
    'difficult issue, what your approach was, and what you learned from it?';
const MIC_CONSTRAINTS = {
    // The interview's own microphone settings, so the meter hears what the interview hears.
    noiseSuppression: true,
    echoCancellation: true,
    autoGainControl: true,
};
function newAudioContext() {
    const Ctor = window.AudioContext ||
        window.webkitAudioContext;
    return new Ctor();
}
const Slider = ({ label, value, unit = '', field, step, hint, onChange }) => {
    const [min, max] = BARGE_IN_BOUNDS[field];
    return (_jsxs("label", { className: 'block text-xs font-medium text-dash-muted', children: [label, ":", ' ', _jsxs("span", { className: 'text-dash-text', children: [field === 'probability' ? value.toFixed(2) : value, unit] }), _jsx("input", { type: 'range', min: min, max: max, step: step, value: value, onChange: (e) => onChange(Number(e.target.value)), className: 'mt-1 w-full', "aria-label": label }), _jsx("span", { className: 'mt-0.5 block text-[11px] font-normal text-dash-muted', children: hint })] }));
};
const BargeInTestPanel = () => {
    const { showToast } = useToast();
    const { data: settings } = useVadSettings();
    const save = useSaveVadSettings();
    const { data: providers } = useAdminVoiceProviders();
    const [form, setForm] = useState(Object.assign({}, DEFAULT_BARGE_IN_CONFIG));
    const [dirty, setDirty] = useState(false);
    const [running, setRunning] = useState(false);
    const [starting, setStarting] = useState(false);
    const [modelError, setModelError] = useState(null);
    const [prob, setProb] = useState(0);
    const [level, setLevel] = useState(-100);
    const [peakLevel, setPeakLevel] = useState(-100);
    const [interrupts, setInterrupts] = useState(0);
    const [flash, setFlash] = useState(false);
    const [aiPlaying, setAiPlaying] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiNote, setAiNote] = useState(null);
    const ctxRef = useRef(null);
    const streamRef = useRef(null);
    const procRef = useRef(null);
    const controllerRef = useRef(null);
    const aiSourceRef = useRef(null);
    const aiBufferRef = useRef(null);
    const playingFallbackRef = useRef(false);
    const readoutRef = useRef({ prob: 0, level: -100, peak: -100, at: 0 });
    const formRef = useRef(form);
    formRef.current = form;
    // Start from the saved values (until the admin starts editing).
    useEffect(() => {
        if (settings && !dirty)
            setForm(fromAdminSettings(settings));
    }, [settings, dirty]);
    // Edits apply to the running meter immediately.
    useEffect(() => {
        var _a;
        (_a = controllerRef.current) === null || _a === void 0 ? void 0 : _a.setConfig(form);
    }, [form]);
    const update = (patch) => {
        setDirty(true);
        setForm((prev) => normalizeBargeInConfig(Object.assign(Object.assign({}, prev), patch)));
    };
    const stopAi = () => {
        var _a;
        try {
            (_a = aiSourceRef.current) === null || _a === void 0 ? void 0 : _a.stop();
        }
        catch (_b) {
            /* already stopped */
        }
        aiSourceRef.current = null;
        if (typeof window !== 'undefined' && window.speechSynthesis)
            window.speechSynthesis.cancel();
        setAiPlaying(false);
    };
    const stopMeter = () => {
        var _a, _b, _c, _d;
        stopAi();
        (_a = procRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        procRef.current = null;
        (_b = streamRef.current) === null || _b === void 0 ? void 0 : _b.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        void ((_c = controllerRef.current) === null || _c === void 0 ? void 0 : _c.dispose());
        controllerRef.current = null;
        void ((_d = ctxRef.current) === null || _d === void 0 ? void 0 : _d.close().catch(() => undefined));
        ctxRef.current = null;
        aiBufferRef.current = null;
        setRunning(false);
    };
    useEffect(() => () => stopMeter(), []); // eslint-disable-line react-hooks/exhaustive-deps
    const startMeter = () => __awaiter(void 0, void 0, void 0, function* () {
        setStarting(true);
        setModelError(null);
        try {
            const stream = yield navigator.mediaDevices.getUserMedia({ audio: MIC_CONSTRAINTS });
            const ctx = newAudioContext();
            const controller = new SileroShadowController({
                log: () => undefined,
                onWindow: (p, lvl) => {
                    const r = readoutRef.current;
                    r.prob = p;
                    r.level = lvl;
                    if (lvl > r.peak)
                        r.peak = lvl;
                    const now = performance.now();
                    if (now - r.at > 100) {
                        r.at = now;
                        setProb(r.prob);
                        setLevel(r.level);
                        setPeakLevel(r.peak);
                    }
                },
                onEvent: (event) => {
                    if (event !== 'would_have_confirmed')
                        return;
                    setInterrupts((n) => n + 1);
                    setFlash(true);
                    // Re-arm, like the next assistant turn would.
                    window.setTimeout(() => {
                        var _a;
                        setFlash(false);
                        (_a = controllerRef.current) === null || _a === void 0 ? void 0 : _a.reset();
                    }, 700);
                },
            });
            controller.setConfig(formRef.current);
            controller.start();
            const source = ctx.createMediaStreamSource(stream);
            const proc = ctx.createScriptProcessor(1024, 1, 1);
            proc.onaudioprocess = (e) => {
                var _a;
                // The meter behaves as if the AI is always speaking: every frame is a candidate for
                // an interruption, exactly what the interview checks while the interviewer talks.
                (_a = controllerRef.current) === null || _a === void 0 ? void 0 : _a.observeAssistantFrame(new Float32Array(e.inputBuffer.getChannelData(0)), ctx.sampleRate, 1);
            };
            source.connect(proc);
            proc.connect(ctx.destination);
            ctxRef.current = ctx;
            streamRef.current = stream;
            procRef.current = proc;
            controllerRef.current = controller;
            readoutRef.current = { prob: 0, level: -100, peak: -100, at: 0 };
            setPeakLevel(-100);
            setInterrupts(0);
            setRunning(true);
            window.setTimeout(() => {
                if (controller.status === 'unavailable') {
                    setModelError(controller.snapshot().error || 'Speech model unavailable in this browser.');
                }
            }, 4000);
        }
        catch (err) {
            showToast({
                message: 'Microphone unavailable',
                description: err instanceof Error ? err.message : String(err),
                status: 'error',
            });
            stopMeter();
        }
        finally {
            setStarting(false);
        }
    });
    /** The default provider's real interviewer voice, as an AudioBuffer (cached per meter run). */
    const loadAiVoice = (ctx) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e;
        if (aiBufferRef.current)
            return aiBufferRef.current;
        const usable = (_a = providers === null || providers === void 0 ? void 0 : providers.filter((p) => p.enabled)) !== null && _a !== void 0 ? _a : [];
        const provider = (_b = usable.find((p) => p.is_default)) !== null && _b !== void 0 ? _b : usable[0];
        if (!provider)
            return null;
        const cacheKey = aiVoiceCacheKey(provider.provider_id, provider.default_voice, provider.default_language, AI_PHRASE);
        let audio = readCachedAiVoice(cacheKey);
        if (!audio) {
            const res = yield voiceAdminClient.post(`/admin/voice/providers/${provider.provider_id}/test/tts/`, {
                phrase: AI_PHRASE,
                voice: (_c = provider.default_voice) !== null && _c !== void 0 ? _c : undefined,
                language: (_d = provider.default_language) !== null && _d !== void 0 ? _d : undefined,
            });
            if (!(res === null || res === void 0 ? void 0 : res.ok) || !((_e = res.chunks) === null || _e === void 0 ? void 0 : _e.length))
                throw new Error((res === null || res === void 0 ? void 0 : res.error) || 'no audio');
            audio = { chunks: res.chunks, sample_rate: res.sample_rate || 24000 };
            writeCachedAiVoice(cacheKey, audio);
        }
        const res = audio;
        const parts = res.chunks.map((c) => pcm16Base64ToAudioBuffer(c, ctx, res.sample_rate || 24000));
        const total = parts.reduce((n, b) => n + b.length, 0);
        const joined = ctx.createBuffer(1, total, parts[0].sampleRate);
        let offset = 0;
        parts.forEach((b) => {
            joined.copyToChannel(b.getChannelData(0), 0, offset);
            offset += b.length;
        });
        aiBufferRef.current = joined;
        return joined;
    });
    const playAi = () => __awaiter(void 0, void 0, void 0, function* () {
        const ctx = ctxRef.current;
        if (!ctx)
            return;
        setAiLoading(true);
        setAiNote(null);
        try {
            const buffer = yield loadAiVoice(ctx);
            if (buffer) {
                const src = ctx.createBufferSource();
                src.buffer = buffer;
                src.loop = true;
                src.connect(ctx.destination);
                src.start();
                aiSourceRef.current = src;
                setAiPlaying(true);
                return;
            }
            throw new Error('no voice provider configured');
        }
        catch (err) {
            // Fallback: the browser's own voice. Echo cancellation may treat it differently from
            // the interview's audio, so say so.
            if (window.speechSynthesis) {
                const speak = () => {
                    const u = new SpeechSynthesisUtterance(AI_PHRASE);
                    u.onend = () => {
                        if (aiSourceRef.current === null &&
                            window.speechSynthesis &&
                            playingFallbackRef.current)
                            speak();
                    };
                    window.speechSynthesis.speak(u);
                };
                playingFallbackRef.current = true;
                speak();
                setAiPlaying(true);
                setAiNote(`Using the browser's built-in voice (${err instanceof Error ? err.message : 'provider unavailable'}). ` +
                    'Results for the AI voice may differ slightly from a real interview.');
            }
            else {
                setAiNote('Could not play an AI voice in this browser.');
            }
        }
        finally {
            setAiLoading(false);
        }
    });
    const toggleAi = () => {
        if (aiPlaying) {
            playingFallbackRef.current = false;
            stopAi();
        }
        else {
            void playAi();
        }
    };
    const onSave = () => {
        save.mutate(toAdminPayload(form), {
            onSuccess: (saved) => {
                setDirty(false);
                setForm(fromAdminSettings(saved));
                showToast({
                    message: 'Interruption settings saved',
                    description: 'The next interviews use them.',
                    status: 'success',
                });
            },
            onError: (err) => showToast({
                message: 'Could not save interruption settings',
                description: err instanceof Error ? err.message : String(err),
                status: 'error',
            }),
        });
    };
    const onResetDefaults = () => {
        setDirty(true);
        setForm(Object.assign({}, DEFAULT_BARGE_IN_CONFIG));
    };
    const aboveFloor = level >= form.min_level_dbfs;
    const voiceLike = prob >= form.probability;
    return (_jsxs("div", { className: 'space-y-4 rounded-xl border border-dash-border p-4', "data-testid": 'barge-in-panel', children: [_jsxs("div", { children: [_jsx("h3", { className: 'text-sm font-semibold text-dash-text', children: "Interruption config (candidate cutting in on the AI)" }), _jsxs("p", { className: 'mt-1 text-sm text-dash-muted', children: ["Interruptions are decided in the candidate's browser, separately from turn detection above. Start the meter, speak at your microphone, then have someone talk behind you \u2014 and watch whether it ", _jsx("strong", { children: "would interrupt" }), ". Use ", _jsx("em", { children: "Play AI voice" }), " to test while the interviewer is speaking."] })] }), _jsxs("div", { className: 'grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3', children: [_jsx(Slider, { label: 'Speech probability', field: 'probability', value: form.probability, step: 0.01, hint: 'How sure the model must be that a sound is speech.', onChange: (v) => update({ probability: v }) }), _jsx(Slider, { label: 'Sustained speech', field: 'onset_ms', value: form.onset_ms, unit: ' ms', step: 32, hint: 'How long speech must last before the AI is cut off.', onChange: (v) => update({ onset_ms: v }) }), _jsx(Slider, { label: 'Minimum loudness', field: 'min_level_dbfs', value: form.min_level_dbfs, unit: ' dBFS', step: 1, hint: describeFloor(form.min_level_dbfs), onChange: (v) => update({ min_level_dbfs: v }) }), _jsx(Slider, { label: 'Gap that abandons', field: 'abandon_ms', value: form.abandon_ms, unit: ' ms', step: 32, hint: 'A pause this long drops a half-started interruption.', onChange: (v) => update({ abandon_ms: v }) }), _jsx(Slider, { label: 'Max onset window', field: 'max_window_ms', value: form.max_window_ms, unit: ' ms', step: 32, hint: 'Kept at least 2 \u00D7 sustained + 32 ms automatically.', onChange: (v) => update({ max_window_ms: v }) })] }), _jsxs("div", { className: 'space-y-3 rounded-lg bg-dash-surface p-3', children: [_jsxs("div", { className: 'flex flex-wrap items-center gap-2', children: [!running ? (_jsx(Button, { size: 'sm', onClick: startMeter, disabled: starting, rightIcon: starting ? (_jsx(Loader2, { className: 'h-4 w-4 animate-spin' })) : (_jsx(Mic, { className: 'h-4 w-4' })), children: "Start meter" })) : (_jsx(Button, { size: 'sm', variant: 'outline', onClick: stopMeter, rightIcon: _jsx(Square, { className: 'h-4 w-4' }), children: "Stop meter" })), _jsx(Button, { size: 'sm', variant: 'outline', onClick: toggleAi, disabled: !running || aiLoading, rightIcon: aiLoading ? (_jsx(Loader2, { className: 'h-4 w-4 animate-spin' })) : aiPlaying ? (_jsx(VolumeX, { className: 'h-4 w-4' })) : (_jsx(Volume2, { className: 'h-4 w-4' })), children: aiPlaying ? 'Stop AI voice' : 'Play AI voice' }), _jsxs("span", { className: 'ml-auto text-xs text-dash-muted', children: ["Interruptions: ", interrupts] })] }), aiNote && _jsx("p", { className: 'text-xs text-dash-muted', children: aiNote }), modelError && (_jsxs("p", { className: 'text-xs text-dash-dangerInk', children: ["Speech model unavailable: ", modelError] })), _jsx("div", { "data-testid": 'barge-in-verdict', className: `rounded-md px-3 py-2 text-center text-sm font-semibold transition-colors ${flash ? 'bg-dash-danger/15 text-dash-dangerInk' : 'bg-dash-active text-dash-muted'}`, children: flash
                            ? 'Would interrupt the AI'
                            : running
                                ? 'Listening — not interrupting'
                                : 'Meter stopped' }), _jsxs("div", { className: 'grid grid-cols-1 gap-3 md:grid-cols-3', children: [_jsxs("div", { className: 'text-xs text-dash-muted', children: ["Speech probability:", ' ', _jsx("span", { className: voiceLike ? 'text-dash-text' : '', children: prob.toFixed(2) }), _jsx("div", { className: 'mt-1 h-2 rounded bg-dash-active', children: _jsx("div", { className: `h-2 rounded ${voiceLike ? 'bg-dash-accent' : 'bg-dash-muted'}`, style: { width: `${Math.round(prob * 100)}%` } }) })] }), _jsxs("div", { className: 'text-xs text-dash-muted', children: ["Loudness:", ' ', _jsxs("span", { className: aboveFloor ? 'text-dash-text' : '', children: [level.toFixed(0), " dBFS"] }), _jsx("div", { className: 'mt-1 h-2 rounded bg-dash-active', children: _jsx("div", { className: `h-2 rounded ${aboveFloor ? 'bg-dash-accent' : 'bg-dash-muted'}`, style: { width: `${Math.max(0, Math.min(100, level + 100))}%` } }) })] }), _jsxs("div", { className: 'text-xs text-dash-muted', children: ["Loudest seen: ", _jsxs("span", { className: 'text-dash-text', children: [peakLevel.toFixed(0), " dBFS"] }), _jsx("span", { className: 'mt-1 block text-[11px]', children: "Compare your voice with the background's to pick the floor." })] })] })] }), _jsxs("div", { className: 'flex flex-wrap items-center justify-between gap-2 border-t border-dash-border pt-3', children: [_jsx("p", { className: 'text-xs text-dash-muted', children: dirty
                            ? 'Unsaved changes — the meter already uses them.'
                            : 'These are the values interviews use now.' }), _jsxs("div", { className: 'flex gap-2', children: [_jsx(Button, { size: 'sm', variant: 'outline', onClick: onResetDefaults, rightIcon: _jsx(RotateCcw, { className: 'h-4 w-4' }), children: "Reset to defaults" }), _jsx(Button, { size: 'sm', onClick: onSave, disabled: !dirty || save.isPending, rightIcon: save.isPending ? (_jsx(Loader2, { className: 'h-4 w-4 animate-spin' })) : (_jsx(Save, { className: 'h-4 w-4' })), children: "Save for interviews" })] })] })] }));
};
export default BargeInTestPanel;
